import {
    PublicPriorityEmailAction,
    PublicPriorityReferralAction
} from "@therapprove/common";
import { FC, useState } from "react";

interface DialogButton {
    text: string;
    disabled?: boolean;
    isLoading?: boolean;
    variant?: "primary" | "secondary";
    emailRecordAction?: PublicPriorityEmailAction;
    referralAction?: PublicPriorityReferralAction;
    onClick?: () => void;
}

export interface DialogProps {
    title: string;
    description: string;
    icon: string;
    buttons?: DialogButton[];
    isLoading?: boolean;
    showAgreement?: boolean;
    successMessage?: string;
    failureMessage?: string;
    isSuccessful?: boolean | null;
    onEmailRecordClick?: (action: PublicPriorityEmailAction) => void;
    onReferralClick?: (action: PublicPriorityReferralAction) => void;
}

export const Dialog: FC<DialogProps> = ({
    title,
    description,
    icon,
    buttons = [],
    showAgreement = true,
    successMessage,
    failureMessage,
    isSuccessful = null,
    onEmailRecordClick,
    onReferralClick
}) => {
    const [isChecked, setIsChecked] = useState<boolean>(false);

    return (
        <div className="flex justify-center items-center h-screen">
            <div className="z-10 rounded-xl max-w-[600px] relative mx-auto shadow-lg bg-white">
                <>
                    <div className="w-[50px] h-[50px] md:w-[80px] md:h-[80px] flex-grow-0 flex-shrink-0 rounded-full absolute -top-5 md:-right-5 -right-2 bg-teal-400 flex items-center justify-center">
                        <span className="material-symbols-outlined text-white text-4xl md:text-5xl">
                            {icon}
                        </span>
                    </div>
                    <h1 className="text-2xl leading-tight text-center md:text-left bg-teal-100 p-5 md:p-10 md:text-4xl text-balance font-bold items-center flex gap-4">
                        <span>{title}</span>
                    </h1>

                    <div className="p-5 md:p-10">
                        {isSuccessful === null && (
                            <p className="text-sm md:text-base font-medium text-gray-500 text-balance">
                                {description}
                            </p>
                        )}

                        <div
                            className={`p-3 text-sm text-center gap-2 rounded-md overflow-hidden transition-all duration-300 ease-in-out ${
                                isSuccessful === null
                                    ? "max-h-0 opacity-0"
                                    : "max-h-auto py-10 opacity-100 mt-4"
                            } ${
                                isSuccessful
                                    ? "bg-teal-50 text-teal-700 "
                                    : " text-red-700 font-bold"
                            }`}
                        >
                            <div className="flex  justify-center items-center mb-2"></div>
                            {!isSuccessful && (
                                <div className="flex  justify-center items-center mb-2 -mt-4">
                                    <span className="material-symbols-outlined text-4xl flex text-center  text-red-400">
                                        {isSuccessful
                                            ? "sync_saved_locally"
                                            : "error"}
                                    </span>
                                </div>
                            )}
                            <div className="text-balance  font-medium text-base md:text-lg">
                                {isSuccessful ? successMessage : failureMessage}
                            </div>
                            {isSuccessful && (
                                <div className="text-balance text-teal-900 text-xs font-semibold mt-2 opacity-50">
                                    You can safely close this page
                                </div>
                            )}
                        </div>

                        {showAgreement && isSuccessful === null && (
                            <label className="transition-all duration-200 ease-in-out text-sm text-gray-600 flex items-center gap-1  hover:bg-gray-100 p-2 rounded-md">
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    checked={isChecked}
                                    onChange={(e) =>
                                        setIsChecked(e.target.checked)
                                    }
                                />
                                <span>
                                    I agree to the{" "}
                                    <a
                                        href="https://therapprove.com/terms/"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-teal-500 hover:underline"
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    and{" "}
                                    <a
                                        href="https://therapprove.com/privacy-policy/"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-teal-500 hover:underline"
                                    >
                                        Privacy Policy
                                    </a>
                                </span>
                            </label>
                        )}

                        {buttons.length > 0 && isSuccessful === null && (
                            <div className="flex gap-2 mt-5">
                                {buttons.map((button, index) => (
                                    <button
                                        key={index}
                                        className={`w-full px-4 py-2 rounded-md flex items-center justify-center disabled:opacity-50 ${
                                            button.variant === "primary"
                                                ? "bg-teal-500 text-white"
                                                : "border border-gray-400 text-gray-500"
                                        }`}
                                        onClick={() => {
                                            if (button.onClick) {
                                                button.onClick();
                                            }
                                            if (
                                                onEmailRecordClick &&
                                                button.emailRecordAction
                                            ) {
                                                onEmailRecordClick(
                                                    button.emailRecordAction
                                                );
                                            }
                                            if (
                                                onReferralClick &&
                                                button.referralAction
                                            ) {
                                                onReferralClick(
                                                    button.referralAction
                                                );
                                            }
                                        }}
                                        disabled={
                                            button.disabled ||
                                            button.isLoading ||
                                            (showAgreement && !isChecked)
                                        }
                                    >
                                        {button.isLoading ? (
                                            <span className="material-symbols-outlined animate-spin">
                                                progress_activity
                                            </span>
                                        ) : (
                                            button.text
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            </div>
        </div>
    );
};
